import {IconButton, ListItemIcon, Popover, Tooltip} from "@mui/material";
import {Close, DragIndicator, Settings} from "@mui/icons-material";
import React, {useState} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import {closestCenter, DndContext, DragOverlay, PointerSensor, useSensor, useSensors} from "@dnd-kit/core";
import {arrayMove, SortableContext, useSortable, verticalListSortingStrategy} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {makeHeader} from "../../Utils/gridUtils";
import {updateUserSetting} from "../../../API/userSettings";
import {LoadingButton} from "@mui/lab";

const SortableColumnItem = ({ column, onToggle }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: column.column });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <ListItem
            ref={setNodeRef}
            style={style}
            dense
            sx={{
                py: 0.5,
                borderRadius: 1,
                border: isDragging ? '1px dashed' : '1px solid transparent',
                borderColor: isDragging ? 'primary.main' : 'transparent',
                backgroundColor: isDragging ? 'action.selected' : 'transparent',
                boxShadow: isDragging ? 1 : 0,
                '&:hover': {
                    backgroundColor: 'action.hover',
                },
                transition: 'all 0.2s ease',
            }}
        >
            <ListItemIcon
                sx={{
                    minWidth: 32,
                    cursor: isDragging ? 'grabbing' : 'grab',
                }}
                {...attributes}
                {...listeners}
            >
                <DragIndicator
                    fontSize="small"
                    color="action"
                    sx={{
                        opacity: 0.6,
                        '&:hover': {
                            opacity: 1,
                        }
                    }}
                />
            </ListItemIcon>
            <ListItemIcon sx={{ minWidth: 32 }}>
                <Checkbox
                    edge="start"
                    checked={column.visible}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                    onChange={() => onToggle(column.column)}
                />
            </ListItemIcon>

            <ListItemText
                primary={makeHeader(column.column)}
                primaryTypographyProps={{
                    variant: 'body2',
                }}
            />
        </ListItem>
    );
};
// Drag Overlay Component for better visual feedback
const ColumnDragOverlay = ({ activeColumn }) => {
    if (!activeColumn) return null;

    return (
        <ListItem
            dense
            sx={{
                py: 0.5,
                borderRadius: 1,
                backgroundColor: 'background.paper',
                border: '1px dashed',
                borderColor: 'primary.main',
                boxShadow: 2,
                width: 280,
            }}
        >
            <ListItemIcon sx={{ minWidth: 32 }}>
                <DragIndicator fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemIcon sx={{ minWidth: 32 }}>
                <Checkbox
                    edge="start"
                    checked={activeColumn.visible}
                    size="small"
                    disabled
                />
            </ListItemIcon>
            <ListItemText
                primary={activeColumn.label}
                primaryTypographyProps={{
                    variant: 'body2',
                    color: 'primary',
                }}
            />
        </ListItem>
    );
};

const GridColumnsControl = ({columns, onColumnToggle, onColumnReorder, table}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [activeId, setActiveId] = useState(null);

    const open = Boolean(anchorEl);
    const id = open ? 'columns-popover' : undefined;
    const activeColumn = columns.find(col => col.column === activeId);

    const sensors = useSensors(
        useSensor(PointerSensor),
    );

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setActiveId(null);
    }

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);

        if (active.id !== over?.id) {
            const oldIndex = columns.findIndex((item) => item.column === active.id);
            const newIndex = columns.findIndex((item) => item.column === over.id);

            const reorderedColumns = arrayMove(columns, oldIndex, newIndex);
            onColumnReorder(reorderedColumns);
        }
    };

    const saveColumns = () => {
        const user = JSON.parse(localStorage.getItem("user"));
        const visibleColumns = columns.filter((column) => (column.visible)).map(column => column.column);

        if (user.tableSettings.hasOwnProperty(table)) {
            user.tableSettings[table].columns = visibleColumns;
        } else {
            user.tableSettings[table] = {
                "columns": visibleColumns,
                "filters": {sort: "created_at"},
                "searchParameters": {text: "", searchByColumn: ""}
            };
        }

        localStorage.setItem("user", JSON.stringify(user)); // Update local storage
        updateUserSetting(user).then(() => {
            setAnchorEl(null);
            setActiveId(null);
        });
    }

    return (
        <>
            <Tooltip title="Manage Columns">
                <IconButton
                    aria-describedby={id}
                    onClick={handleClick}
                    color="#626262"
                    size="medium">
                        <Settings/>
                </IconButton>
            </Tooltip>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                >
                <Box sx={{ p: 2 }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1
                    }}>
                        <Typography variant="h6" component="h3">
                            Columns
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={handleClose}
                            aria-label="Close"
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    </Box>
                    <Divider sx={{ mb: 1 }} />
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}>

                        <SortableContext items={columns.map(col => col.column)} strategy={verticalListSortingStrategy}>
                            <List dense sx={{
                                overflow: 'none' }}>
                                {
                                    columns.map((column, index) => (
                                        column.column &&
                                        <SortableColumnItem
                                                key={column.column}
                                            column={column}
                                            index={index}
                                            onToggle={onColumnToggle}
                                        />
                                    ))
                                }
                            </List>
                        </SortableContext>

                        <DragOverlay>
                            {activeColumn ? <ColumnDragOverlay/> : null}
                        </DragOverlay>
                    </DndContext>


                    <Box  sx={{
                        display: 'flex',
                        justifyContent: 'flex-end'}}>
                        <LoadingButton variant="contained" onClick={saveColumns}>
                            Save Changes
                        </LoadingButton>
                    </Box>
                </Box>
            </Popover>
        </>
    )
}

export default GridColumnsControl;